import React, { useState } from 'react';
import withStyles from 'react-jss';
import { Link, withRouter } from 'react-router-dom';
import { queryObject, queryColumns } from '../../services/Utils/query';
import { StorageLink } from '../../components/Link/StorageLink/StorageLink';
import { calibrationText, calibrationTextWithExpire } from '../../services/Utils/Calibration';
import SortArrows from '../../components/SortArrows/SortArrows';
import { Icon } from '../../components/Icon/Icon';
import { ConditionDefect } from '../../constants';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import { download } from '../../components/DownloadCSV/Download';

const styles = {
  list: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, auto) 60px',
  },
  listWithDelete: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, auto) 60px,70px',
  },
  heading: {
    fontWeight: 'bold',
    padding: '10px',
    position: 'relative',
  },
  rowTight: {
    borderBottom: '1px solid black',
  },
  row: {
    padding: '10px',
    borderBottom: '1px solid black',
  },
  filterRow: {
    padding: '1px',
    position: 'relative',
    gridGap: '1px',
  },
  filterIcon: {
    cursor: 'pointer',
    padding: '10px',
    position: 'relative',
  },
  listEmpty: {
    padding: '10px',
    gridColumn: '1/9',
    justifySelf: 'center',
  },
  listEmptyWithDelete: {
    padding: '10px',
    gridColumn: '1/10',
    justifySelf: 'center',
  },
  lineDeleted: {
    '& div': {
      textDecoration: 'line-through',
    },
    textDecoration: 'line-through',
  },
  rowWrapper: {
    display: 'contents',
    position: 'relative',
    '&:hover div': {
      backgroundColor: 'var(--alert-notification-backgroundColor)',
    },
  },
  danger: {
    '& div': {
      backgroundColor: 'var(--alert-danger-backgroundColor)',
    },
  },
  warning: {
    '& div': {
      backgroundColor: 'var(--alert-warning-backgroundColor)',
    },
  },
  iconRow: {
    borderBottom: '1px solid black',
    padding: '5px',
  },
  alert: {
    backgroundColor: 'var(--alert-danger-backgroundColor)',
  },
};

const assetQueryKeys = [
  'id',
  'owner',
  'productId',
  'productName',
  'serialNumber',
  'tagNumber',
  'storageId',
  'storageName',
  'rental',
  'rentalText',
  'calibrationText',
  'calibrationTextWithExpire',
  'product.id',
  'product.name',
  'product.model',
  'product.description',
  'product.category',
  'product.calibrationInterval',
  'product.price',
  'product.weight',
  'product.dimensions',
  'product.producer',
  'storage.id',
  'storage.name',
  'storage.lineageNames',
];

const AssetList = ({ assets, classes, filter, columnFilter }) => {
  const [sort, setSort] = useState(() => (a, b) => (a.date < b.date ? 1 : -1));
  const [selectedTag, setSelectedTag] = useState('');

  function handleSort(dynamicSort, tag) {
    setSort(() => dynamicSort);
    setSelectedTag(tag);
  }

  const toCSV = (assets) => {
    let csvAssets = assets.sort(sort).map((row) => [
      row.product.name,
      row.storageId
        ? row.storage.lineageNames.reduce((a, b) => {
            return a + ' / ' + b;
          })
        : 'In transit',
      row.serialNumber,
      row.tagNumber,
      row.rentalText,
      row.owner,
      row.calibrationText,
      row.condition === ConditionDefect ? 'Need Service' : '',
      row.deleted ? 'Deleted' : '',
    ]);
    csvAssets.unshift([
      'Asset model name',
      'Storage',
      'Serial',
      'Tagnumber',
      'Rental',
      'Owner',
      'Calibration Date',
      'Status',
      'Deleted',
    ]);
    return csvAssets;
  };
  const handleDownloadCSV = (assets) => {
    let separator = ',';
    download({
      content: toCSV(assets)
        .map((v) =>
          v
            .map((v) => v.toString())
            .map((v) => {
              if (v.indexOf('\n') !== -1 || v.indexOf(separator) !== -1 || v.indexOf('"') !== -1) {
                return `"${v.replace('"', '""')}"`;
              }
              return v;
            })
            .join(separator),
        )
        .join('\n'),
      fileName: 'assetlist.csv',
      mimeType: 'text/csv;encoding:UTF-8',
    });
  };

  const returnClass = (s) => {
    if (s?.includes('expired')) {
      return classes.danger;
    } else if (s?.includes('Not calibrated')) {
      return classes.danger;
    } else if (s?.includes('days')) {
      return classes.warning;
    }
    return '';
  };

  function isSelected(tag) {
    return selectedTag === tag;
  }

  const [filterProductName, setFilterProductName] = useState('');
  const [filterStorageName, setFilterStorageName] = useState('');
  const [filterSerialNumber, setFilterSerialNumber] = useState('');
  const [filterTagNumber, setFilterTagNumber] = useState('');
  const [filterRentalText, setFilterRentalText] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterCalibrationText, setFilterCalibrationText] = useState('');
  const [filterCondition, setFilterCondition] = useState(4);
  const [filterConditionIcon, setFilterConditionIcon] = useState(
    <Icon onClick={() => handleFilterConditionChange(4)} title='Click to filter'>
      all_inclusive
    </Icon>,
  );
  const [filterDeleted, setFilterDeleted] = useState(0);
  const [filterDeletedIcon, setFilterDeletedIcon] = useState(
    <Icon onClick={() => handleFilterDeletedChange(0)} title='Not showing deleted'>
      check
    </Icon>,
  );

  const handleFilterProductNameChange = (e) => {
    setFilterProductName(e.target.value);
  };
  const handleFilterStorageNameChange = (e) => {
    setFilterStorageName(e.target.value);
  };
  const handleFilterSerialNumberChange = (e) => {
    setFilterSerialNumber(e.target.value);
  };
  const handleFilterTagNumberChange = (e) => {
    setFilterTagNumber(e.target.value);
  };
  const handleFilterRentalTextChange = (e) => {
    setFilterRentalText(e.target.value);
  };
  const handleFilterOwnerChange = (e) => {
    setFilterOwner(e.target.value);
  };
  const handleFilterCalibrationTextChange = (e) => {
    setFilterCalibrationText(e.target.value);
  };

  function handleFilterConditionChange(newState) {
    if (newState === 0) {
      setFilterCondition(4);
      setFilterConditionIcon(
        <Icon onClick={() => handleFilterConditionChange(4)} title='Click to filter'>
          all_inclusive
        </Icon>,
      );
    } else if (newState === 1) {
      setFilterCondition(0);
      setFilterConditionIcon(
        <Icon onClick={() => handleFilterConditionChange(0)} title='Showing working assets'>
          check
        </Icon>,
      );
    } else if (newState === 4) {
      setFilterCondition(1);
      setFilterConditionIcon(
        <Icon onClick={() => handleFilterConditionChange(1)} title='Showing faulty'>
          warning
        </Icon>,
      );
    }
  }

  function handleFilterDeletedChange(newDeleteState) {
    if (newDeleteState === 0) {
      setFilterDeleted(4);
      setFilterDeletedIcon(
        <Icon onClick={() => handleFilterDeletedChange(4)} title='Showing all , inclusive deleted'>
          all_inclusive
        </Icon>,
      );
    } else if (newDeleteState === 1) {
      setFilterDeleted(0);
      setFilterDeletedIcon(
        <Icon onClick={() => handleFilterDeletedChange(0)} title='Not showing deleted'>
          check
        </Icon>,
      );
    } else if (newDeleteState === 4) {
      setFilterDeleted(1);
      setFilterDeletedIcon(
        <Icon onClick={() => handleFilterDeletedChange(1)} title='Showing only deleted'>
          delete
        </Icon>,
      );
    }
  }

  var filterList = {};
  if (filterProductName !== '') {
    filterList['productName'] = filterProductName;
  }
  if (filterStorageName !== '') {
    filterList['storageName'] = filterStorageName;
  }
  if (filterSerialNumber !== '') {
    filterList['serialNumber'] = filterSerialNumber;
  }
  if (filterTagNumber !== '') {
    filterList['tagNumber'] = filterTagNumber;
  }
  if (filterRentalText !== '') {
    filterList['rentalText'] = filterRentalText;
  }
  if (filterOwner !== '') {
    filterList['owner'] = filterOwner;
  }
  if (filterCalibrationText !== '') {
    filterList['calibrationText'] = filterCalibrationText;
  }
  if (filterCondition !== 4) {
    filterList['condition'] = filterCondition;
  }
  let assetList = assets.map((asset) => {
    return {
      ...asset,
      rentalText: asset.rental ? 'Rental' : '',
      calibrationText: calibrationText(asset.calibrationDate, asset.product.calibrationInterval),
      storageName: asset.storage?.lineageNames.join(' '),
      productName: asset.product.name,
      calibrationTextWithExpire: calibrationTextWithExpire(
        asset.calibrationDate,
        asset.product.calibrationInterval,
      ),
    };
  });

  assetList = queryObject(assetList, filter, assetQueryKeys).filter(
    (row) =>
      !(row.deleted === true && filterDeleted === 0) &&
      !(row.deleted === false && filterDeleted === 1),
  );
  if (columnFilter) {
    assetList = queryColumns(assetList, filterList);
  }
  return (
    <div className={columnFilter ? classes.listWithDelete : classes.list}>
      <div className={classes.heading}>
        Asset
        <SortArrows
          handleSort={handleSort}
          tag='productName'
          selected={isSelected('productName')}
        />
      </div>
      <div className={classes.heading}>
        Storage
        <SortArrows
          handleSort={handleSort}
          tag='storageName'
          selected={isSelected('storageName')}
        />
      </div>
      <div className={classes.heading}>
        S/N
        <SortArrows
          handleSort={handleSort}
          tag='serialNumber'
          selected={isSelected('serialNumber')}
        />
      </div>
      <div className={classes.heading}>
        T/N
        <SortArrows handleSort={handleSort} tag='tagNumber' selected={isSelected('tagNumber')} />
      </div>
      <div className={classes.heading}>
        Rental
        <SortArrows handleSort={handleSort} tag='rental' selected={isSelected('rental')} />
      </div>
      <div className={classes.heading}>
        Owner
        <SortArrows handleSort={handleSort} tag='owner' selected={isSelected('owner')} />
      </div>
      <div className={classes.heading}>
        Calibration Date
        <SortArrows
          handleSort={handleSort}
          tag='calibrationDate'
          selected={isSelected('calibrationDate')}
        />
      </div>
      <div className={classes.heading}>
        <Icon title='Status'>warning</Icon>
        <SortArrows handleSort={handleSort} tag='condition' selected={isSelected('condition')} />
      </div>
      {columnFilter && (
        <div className={classes.heading}>
          <Icon title='Deleted'>delete</Icon>
          <SortArrows handleSort={handleSort} tag='deleted' selected={isSelected('delete')} />
        </div>
      )}
      {columnFilter && (
        <>
          <div className={classes.filterRow}>
            <Input
              className={classes.search}
              placeholder={'Filter'}
              onChange={handleFilterProductNameChange}
              value={filterProductName}
            />
          </div>
          <div className={classes.filterRow}>
            <Input
              className={classes.search}
              placeholder={'Filter'}
              onChange={handleFilterStorageNameChange}
              value={filterStorageName}
            />
          </div>
          <div className={classes.filterRow}>
            <Input
              className={classes.search}
              placeholder={'Filter'}
              onChange={handleFilterSerialNumberChange}
              value={filterSerialNumber}
            />
          </div>
          <div className={classes.filterRow}>
            <Input
              className={classes.search}
              placeholder={'Filter'}
              onChange={handleFilterTagNumberChange}
              value={filterTagNumber}
            />
          </div>
          <div className={classes.filterRow}>
            <Input
              className={classes.search}
              placeholder={'Filter'}
              onChange={handleFilterRentalTextChange}
              value={filterRentalText}
              size='4'
            />
          </div>
          <div className={classes.filterRow}>
            <Input
              className={classes.search}
              placeholder={'Filter'}
              onChange={handleFilterOwnerChange}
              value={filterOwner}
              size='4'
            />
          </div>
          <div className={classes.filterRow}>
            <Input
              className={classes.search}
              placeholder={'Filter'}
              onChange={handleFilterCalibrationTextChange}
              value={filterCalibrationText}
              size='4'
            />
          </div>
          <div className={classes.filterIcon}>{filterConditionIcon}</div>
          <div className={classes.filterIcon}>{filterDeletedIcon}</div>
        </>
      )}
      {assetList
        .sort(sort)
        .slice(0, 100)
        .map((row) => (
          <div
            key={row.id}
            className={
              classes.rowWrapper +
              ' ' +
              (row.deleted && classes.lineDeleted) +
              ' ' +
              (row.condition === ConditionDefect && classes.danger) +
              ' ' +
              returnClass(row.calibrationTextWithExpire)
            }
          >
            <div className={classes.row}>
              {' '}
              <Link to={'../assets/' + row.id}>{row.product.name}</Link>
            </div>
            <div className={classes.row}>{row.deleted ? '' : <StorageLink {...row.storage} />}</div>
            <div className={classes.row}>{row.serialNumber}</div>
            <div className={classes.row}>{row.tagNumber}</div>
            <div className={classes.row}>{row.rentalText}</div>
            <div className={classes.row}>{row.owner}</div>
            <div className={classes.iconRow}>
              {row.calibrationTextWithExpire.includes('d') && (
                <Icon title={row.calibrationTextWithExpire}>warning</Icon>
              )}
              {row.calibrationText}
            </div>
            <div className={classes.iconRow}>
              {row.condition === ConditionDefect && <Icon title='Need Service'>warning</Icon>}
            </div>
            {columnFilter && (
              <div className={classes.iconRow}>
                {row.deleted === true && <Icon title='Deleted'>deleted</Icon>}
              </div>
            )}
          </div>
        ))}
      {assetList.length > 100 && (
        <div className={columnFilter ? classes.listEmptyWithDelete : classes.listEmpty}>
          Showing 100 of {assetList.length} assets, please use filter to limit result
        </div>
      )}
      {assetList.length <= 100 && (
        <div className={columnFilter ? classes.listEmptyWithDelete : classes.listEmpty}>
          {assetList.length} assets found
        </div>
      )}
      {assetList.length > 0 && (
        <div className={columnFilter ? classes.listEmptyWithDelete : classes.listEmpty}>
          <Button
            onClick={() => handleDownloadCSV(assetList)}
            circle
            title='Download result as file'
          >
            <Icon>save_alt</Icon>
          </Button>
        </div>
      )}
    </div>
  );
};

export default withRouter(withStyles(styles)(AssetList));
